import React, { useCallback, useMemo, useState } from 'react';
import { SalvButton, SalvProgressCircle, SalvTable, SalvTablePaginationType, Size } from '@salvapp/ui-components';
import { InternalApiCase, InternalApiCaseAttachmentType, InternalApiSortDirection } from '../../api-internal';
import Has from '../../auth/Has';
import { CAN_MODIFY_CASE } from '../../auth/permissions';
import DataLoadingErrorFallback from '../../common/errors/DataLoadingErrorFallback';
import { createColumnHelper, PaginationState, SortingState, Updater } from '@tanstack/react-table';
import { getCasePath } from '../../navigation/helper';
import { useNavigate } from 'react-router';
import AttachToCasesModal from './AttachToCasesModal';
import { useQueryCasesWithAttachment } from '../casesQueryActions';
import { getOrderByAndDirection } from '../../common/sort';
import './AttachmentCases.scss';
import { useQueryCaseRationaleOptions } from '../caseQueryActions';

export const ATTACHMENT_TYPE_LABEL: Record<InternalApiCaseAttachmentType, string> = {
  [InternalApiCaseAttachmentType.PERSON]: 'Person',
  [InternalApiCaseAttachmentType.TRANSACTION]: 'Transaction',
  [InternalApiCaseAttachmentType.SCREENING_ALERT]: 'Screening Alert',
  [InternalApiCaseAttachmentType.MONITORING_ALERT]: 'Monitoring Alert',
  [InternalApiCaseAttachmentType.BRIDGE_ALERT]: 'Bridge Alert',
  [InternalApiCaseAttachmentType.MANUAL_ALERT]: 'Manual Alert',
};

interface AttachmentCasesProps {
  attachmentType: InternalApiCaseAttachmentType;
  externalId: string;
}

const PAGE_SIZE = 5;
const DEFAULT_ORDER_BY = 'createdAt';
const DEFAULT_DIRECTION = InternalApiSortDirection.DESC;

const AttachmentCases = ({ attachmentType, externalId }: AttachmentCasesProps) => {
  const label = ATTACHMENT_TYPE_LABEL[attachmentType];
  const navigate = useNavigate();
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [direction, setDirection] = useState(DEFAULT_DIRECTION);

  const columnSort = useMemo(
    () => ({ id: orderBy, desc: direction === InternalApiSortDirection.DESC }),
    [orderBy, direction]
  );

  const setPagination = useCallback((newPagination: PaginationState) => {
    setPage(newPagination.pageIndex);
  }, []);

  const setSorting = useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      const { orderBy: newOrderBy = DEFAULT_ORDER_BY, direction: newDirection = DEFAULT_DIRECTION } =
        getOrderByAndDirection(updaterOrValue, [columnSort]);

      setOrderBy(newOrderBy);
      setDirection(newDirection);
      setPage(0);
    },
    [columnSort]
  );

  const {
    data: casesPage,
    isPending,
    isFetching,
    isError,
    error,
  } = useQueryCasesWithAttachment({
    attachmentType,
    externalId,
    page,
    size: PAGE_SIZE,
    orderBy,
    direction,
  });

  const {
    data: rationaleOptions,
    isPending: isRationaleOptionsPending,
    isError: isRationaleOptionsError,
  } = useQueryCaseRationaleOptions();

  const columnHelper = createColumnHelper<InternalApiCase>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('details.name', {
        id: 'name',
        header: 'Name',
        enableSorting: true,
      }),
      columnHelper.accessor('details.description', {
        id: 'description',
        header: 'Description',
        enableSorting: true,
      }),
      columnHelper.accessor('details.rationaleCode', {
        header: 'Rationale',
        cell: ({ getValue }) => {
          const code = getValue();
          if (isRationaleOptionsPending || isRationaleOptionsError) {
            return [];
          }
          return rationaleOptions.find((option) => option.value === code)!.label;
        },
      }),
    ],
    [rationaleOptions, isRationaleOptionsPending, isRationaleOptionsError]
  );

  if (isError) {
    return <DataLoadingErrorFallback message='Could not load Cases' error={error} />;
  }

  return (
    <div className='salv-attachment-cases'>
      {isPending ? (
        <SalvProgressCircle ariaLabel='Loading Cases' />
      ) : (
        <>
          <SalvTable
            title='Cases'
            columns={columns}
            data={casesPage.content}
            isLoading={isFetching}
            emptyMessage={`No cases attached to this ${label}`}
            hover
            rowClick={({ original }) => navigate(getCasePath(original.handle))}
            pagination={{
              type: SalvTablePaginationType.PAGINATION,
              total: casesPage.total,
              state: { pageIndex: page, pageSize: PAGE_SIZE },
              setPagination,
            }}
            sorting={{
              state: [columnSort],
              setSorting,
            }}
            actions={
              <Has allPermissions={[CAN_MODIFY_CASE]}>
                <SalvButton
                  variant='secondary'
                  size={Size.SMALL}
                  onClick={() => setShowAttachModal(true)}
                  label='Attach to Case'
                />
              </Has>
            }
          />
          {showAttachModal && (
            <AttachToCasesModal
              attachmentType={attachmentType}
              externalId={externalId}
              onClose={() => setShowAttachModal(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AttachmentCases;
